import { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import { loginSchema } from '../../../validations/login'
import FormInput from '../../../components/Input'
import DefaultButton from '../../../components/Button/Button'
import { endpoints, postFunc } from '../../../api'
import { setLocals } from '../../../utils/local'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { showToast } from '../../../utils/toast'
import { Link as RouterLink } from 'react-router-dom'
import useAuth from '../../../hooks/useAuth'
import { ILoginOptions, LoginPayloadType, LoginResponse } from '../../../interfaces/login'
import usePermission from '../../../hooks/usePermission'
import SafeltoLogo from '../../../assets/icons/SafeltoLogo'
import SafeltoShortLogo from '../../../assets/icons/SafeltoShortLogo'
import CodeLoginFormModal from '../../../components/Modal/Profile/CodeLoginFormModal'
import Metadata from '../../../components/Metadata'
import ResidentPwaNotice from '../../../components/ResidentPwaNotice'

  const loginMetadata = {
    title: 'Login - Safelto',
    description: 'Login to your Safelto account to manage your estate, residents, and staff. Secure login for property management platform.',
    keywords: ['login', 'Safelto login', 'property management login', 'estate management login'],
    image: '/assets/images/safelto-big.png',
    url: 'https://safelto.com/login',
    type: 'website' as 'website'
  }

  const Login = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { getDefaultRoute } = usePermission()
    const redirectPath = location?.state?.from
    const [loading, setLoading] = useState<boolean>(false)
    useAuth({ to: redirectPath })
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [initialValues] = useState<ILoginOptions>({
      email: '',
      password: '',
    })

    const formik = useFormik({
      initialValues,
      validationSchema: loginSchema,
      onSubmit: async (data) => {
        if (loading) return

        const { email, password } = data
        let loginData: LoginPayloadType = { email, password }
        setLoading(true)
        try {
          const data = await postFunc<LoginPayloadType, LoginResponse>(endpoints.login, loginData)
          if (data.requires2Fa) {
            setLocals({
              token: data.twoFaToken.token,
            })
            setIsOpen(true)
          } else {
            setLocals({
              token: data.tokens.access.token,
              refreshToken: data.tokens.refresh.token,
              user: data.user,
              isLoggedIn: true,
            })
            const route = getDefaultRoute(data.permissions)
            navigate(redirectPath || route, { replace: true })
          }

          formik.resetForm()
        } catch (err: any) {
          showToast({
            id: 'login-error',
            title: 'Login error',
            description: err.message,
            status: 'error',
          })
        } finally {
          setLoading(false)
        }
      },
    })

    useEffect(() => {
      if (sessionStorage.getItem('passwordResetComplete') === 'true') {
        formik.setFieldValue('password', '')
        sessionStorage.removeItem('passwordResetComplete')

        showToast({
          title: 'Password reset',
          description: 'Please log in with your new password.',
          status: 'success',
          id: 'login-after-reset',
        })
      }
    }, [formik])

    return (
      <>
        <div className="h-screen bg-gray-100 pt-10 px-4 md:px-8 space-y-6 md:space-y-12 overflow-y-auto">
          <Metadata {...loginMetadata} />
        <div>
          <SafeltoLogo />
          <Link to={'/'}>{/* <DarkSafeltoLogo /> */}</Link>
        </div>
        <div className="flex justify-center items-center">
          <div className="p-4 md:p-8 rounded-lg w-full max-w-[500px]">
            <div className="flex flex-col items-center mb-8 md:mb-16">
              <a href="/">
                {/* <img src={Darklogo} alt="Logo" className="w-10 h-10 mb-10" /> */}
                <div className="w-10 h-10 mx-auto">
                  <SafeltoShortLogo />
                </div>
                {/* <img src={SafeltoShortLogo} alt="Logo" className="w-10 h-10 mb-10" /> */}
              </a>
              <h1 className="text-lg font-bold">Your estate details</h1>
              <p className="text-sm text-gray-500">
                Please enter your estate name and address
              </p>
            </div>
            <ResidentPwaNotice
              className="mb-6"
              message="Are you a resident? This sign-in is for estate managers and staff."
              linkText="Sign in to Safelto Resident"
              path="/login"
            />
            <form onSubmit={formik.handleSubmit}>
              <FormInput
                placeholder="Enter email"
                size="lg"
                type="email"
                className="mb-8 md:mb-16"
                value={formik.values.email}
                onChange={formik.handleChange}
                name="email"
                error={formik.errors.email}
              />

              <FormInput
                placeholder="Enter password"
                size="lg"
                type="password"
                value={formik.values.password}
                onChange={formik.handleChange}
                name="password"
                error={formik.errors.password}
                useToggle={true}
              />
              <div className="mt-1 flex justify-end">
                <p className="mr-2">
                  <RouterLink to="/auth/forgot-password" className="text-[#61A5C2]">
                    Forget password
                  </RouterLink>
                </p>
              </div>

              <div className="mt-8">
                <DefaultButton
                  bg="black"
                  loading={loading}
                  loadingText="Submitting..."
                  color="white"
                  size="lg"
                  className="w-full"
                  type="submit"
                  disabled={loading}
                  style={{
                    backgroundColor:
                      !formik.values.email && !formik.values.password
                        ? '#DEE2E6'
                        : 'black',
                  }}
                >
                  Continue
                </DefaultButton>
              </div>
            </form>

            {/* Temporary disable account creation */}
            {/* <div className="mt-6 flex justify-center">
              <p className="mr-2">Don't have an account?</p>
              <a href="/auth/register" className="text-[#61A5C2]">
                Create an account
              </a>
            </div> */}
          </div>
        </div>
      </div>
      {isOpen && (
        <CodeLoginFormModal
          open={isOpen}
          setIsOpen={setIsOpen}
          loading={loading}
          setIsloading={setLoading}
          onSuccess={() => setIsOpen(false)}
        />
      )}
    </>
  )
}

export default Login
